"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import { useSession } from "next-auth/react";
import useSolopreneurProfile, { magasinDossier, useHydrate } from "@/hooks/useSolopreneurProfile";
import { readLocal, writeLocal } from "@/helpers/safeStorage";
import { DOSSIER_VIDE, dossierVide } from "@/lib/tools/dossier";

/**
 * Le dossier suit le compte, quand il y en a un.
 *
 * LA PLUS RÉCENTE DES DEUX VERSIONS L'EMPORTE. Chaque écriture du dossier est
 * datée (majLe), un effacement compris : effacer écrit un dossier vide daté,
 * que le compte garde comme les autres. Un appareil resté sur une ancienne
 * copie ne peut donc pas la faire revenir : elle est plus vieille que
 * l'effacement.
 *
 * UN DOSSIER APPARTIENT AU COMPTE QUI L'A SYNCHRONISÉ. Le navigateur note
 * l'identifiant du dernier compte avec lequel il a échangé. Sur un ordinateur
 * partagé, celui qui se connecte après un autre ne reçoit pas le dossier de
 * son prédécesseur : l'appareil reprend la version de SON compte, ou un
 * dossier vide. Un dossier rempli sans compte ne rejoint un compte que si ce
 * compte n'en a pas encore : il ne remplace jamais des réponses déjà données.
 *
 * SANS COMPTE, RIEN NE PART. Le hook ne fait rien tant que personne n'est
 * connecté : c'est la promesse « gratuits, sans compte » de la rubrique. Il ne
 * fait rien non plus quand `actif` est faux : un outil que le dossier ne
 * remplit pas n'a pas à interroger le compte.
 *
 * Il rend aussi l'adresse de l'agenda auquel s'abonner, que seul le compte
 * peut servir (app/api/tools/agenda/[jeton]).
 */

/** L'identifiant du dernier compte avec lequel cet appareil a synchronisé. */
const CLE_PROPRIETAIRE = "believemy-dossier-compte";

const date = (iso) => (iso ? Date.parse(iso) || 0 : 0);

export default function useDossierCompte(lang = "fr", { actif = true } = {}) {
    const { data: session, status } = useSession();
    const userId = session?.user?.userId ? String(session.user.userId) : null;
    const hydrate = useHydrate();
    const { profil, enregistrer } = useSolopreneurProfile();
    const [agenda, setAgenda] = useState(null);
    const [pret, setPret] = useState(false);
    // La date de la dernière version échangée avec le compte : un changement
    // qui la porte déjà vient du compte, et n'a pas à y retourner.
    const echange = useRef(null);

    const appeler = useCallback(
        async (corps) => {
            const reponse = await fetch("/api/tools/dossier", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ ...corps, locale: lang }),
            });
            if (!reponse.ok) throw new Error(`dossier ${reponse.status}`);
            return reponse.json();
        },
        [lang],
    );

    // À la connexion : lire le compte, puis décider qui l'emporte.
    useEffect(() => {
        if (!actif || !hydrate || status !== "authenticated" || !userId) return undefined;
        let annule = false;
        (async () => {
            try {
                const compte = await appeler({ action: "lire" });
                if (annule) return;
                const local = magasinDossier.lire();
                const proprietaire = readLocal(CLE_PROPRIETAIRE);
                const adopterLeCompte = () => {
                    // Le compte fait foi : sa version, ou un dossier vide s'il
                    // n'en a pas, datée comme lui pour ne pas repartir.
                    const version = compte.dossier || { ...DOSSIER_VIDE };
                    const majLe = compte.majLe || new Date(0).toISOString();
                    echange.current = majLe;
                    enregistrer(version, { majLe });
                };

                if (proprietaire && proprietaire !== userId) {
                    // Le dossier de l'appareil est celui d'un autre compte.
                    adopterLeCompte();
                } else if (!proprietaire) {
                    // Un dossier rempli sans compte : il ne rejoint le compte
                    // que si celui-ci n'a encore rien.
                    if (compte.dossier && !dossierVide(compte.dossier)) {
                        adopterLeCompte();
                    } else if (!dossierVide(local)) {
                        const envoye = await appeler({ action: "enregistrer", dossier: local });
                        if (annule) return;
                        echange.current = local.majLe;
                        setAgenda(envoye.agenda);
                    } else {
                        echange.current = local.majLe;
                    }
                } else if (date(compte.majLe) > date(local.majLe)) {
                    adopterLeCompte();
                } else if (date(local.majLe) > date(compte.majLe)) {
                    // L'appareil est plus récent, effacement compris.
                    const envoye = await appeler({ action: "enregistrer", dossier: local });
                    if (annule) return;
                    echange.current = local.majLe;
                    setAgenda(envoye.agenda);
                } else {
                    echange.current = local.majLe;
                }
                writeLocal(CLE_PROPRIETAIRE, userId);
                if (compte.agenda) setAgenda((a) => a || compte.agenda);
            } catch (e) {
                // Le compte ne répond pas : l'appareil garde son dossier, rien
                // n'est perdu, et la prochaine visite réessaiera.
            } finally {
                if (!annule) setPret(true);
            }
        })();
        return () => {
            annule = true;
        };
    }, [actif, hydrate, status, userId, appeler, enregistrer]);

    // Ensuite : chaque changement local part au compte, une seconde et demie après.
    useEffect(() => {
        if (!actif || !pret || status !== "authenticated" || !profil.majLe || profil.majLe === echange.current) {
            return undefined;
        }
        const minuteur = setTimeout(async () => {
            try {
                const local = magasinDossier.lire();
                const envoye = await appeler({ action: "enregistrer", dossier: local });
                echange.current = local.majLe;
                setAgenda(envoye.agenda);
            } catch (e) {
                // Même chose : on réessaiera au prochain changement.
            }
        }, 1500);
        return () => clearTimeout(minuteur);
    }, [actif, pret, status, profil.majLe, appeler]);

    const renouvelerAgenda = useCallback(async () => {
        const r = await appeler({ action: "renouveler-agenda" });
        setAgenda(r.agenda);
        return r.agenda;
    }, [appeler]);

    // `pret` : le compte a été lu, le dossier de l'appareil est donc le bon. Un
    // écran qui propose de créer un dossier attend ce signal, sinon il le
    // proposerait une seconde à quelqu'un qui en a déjà un.
    return { connecte: status === "authenticated", chargement: status === "loading", pret, agenda, renouvelerAgenda };
}
